import {
  LIMITS,
  PROTOCOL,
  exceedsMessageLimit,
  isReadyMessage,
  isUnloadingMessage,
  parseRequest,
  timeoutFor,
  type GuestEvent,
  type Operation,
  type ProtocolError,
  type RequestEnvelope,
} from '@microapp/sdk';
import { ApiClient, ApiError, type AssetContent } from '../api/client';
import { EventStream } from '../api/events';
import type { PlatformEvent, TaskRequest } from '../api/types';

/**
 * sandbox iframe とAPIの間に立つ信頼側の窓口。
 *
 * ここが唯一の越境点なので、handshakeの検証・operationの検証・frameに固定した
 * workspace/app以外のresource拒否・上限・timeoutを1か所に集めている。
 * Reactに依存させていないのは、この境界をDOMだけでテストできるようにするため。
 */

export type HandshakeRejection =
  | 'source'
  | 'origin'
  | 'protocol'
  /** 置き換えられる前の文書から届いたready。 */
  | 'stale-document'
  | 'no-port'
  | 'already-connected';

export type FrameHostEvent =
  | { type: 'connected' }
  | { type: 'handshake-rejected'; reason: HandshakeRejection }
  | { type: 'request'; operation: Operation; ok: boolean; code: string | null }
  | { type: 'request-rejected'; code: string; message: string }
  /** frameが自分で別文書へ遷移した。portと購読を破棄済み。 */
  | { type: 'navigated' }
  /** client artifactが更新され古いframeになった（412）。再読込が必要。 */
  | { type: 'stale-client' }
  | { type: 'detached'; reason: string };

export interface FrameHostOptions {
  api: ApiClient;
  workspaceId: string;
  appId: string;
  /** GET A/client のETag。RPC/run開始/回答のIf-Matchに使う。 */
  clientDigest: string | null;
  /** このhostが入れたsrcdocのnonce。これと一致するreadyだけを受け付ける。 */
  nonce: string;
  onEvent(event: FrameHostEvent): void;
}

interface Subscription {
  stream: EventStream;
  abort: AbortController;
}

export class FrameHost {
  private iframe: HTMLIFrameElement | null = null;
  private port: MessagePort | null = null;
  /**
   * frameが別文書へ遷移した後は、このhostを終端させる。
   * portをnullへ戻すだけでは、遷移先の文書がreadyを投げて新しいportを受け取れてしまう
   * （WindowProxyはnavigationを跨いで同一で、sandboxのoriginも'null'のまま）。
   */
  private dead = false;
  private inFlight = 0;
  private readonly subscriptions = new Map<string, Subscription>();
  private readonly pendingAborts = new Set<AbortController>();
  private readonly onWindowMessage = (event: MessageEvent) => this.handleWindowMessage(event);

  constructor(private readonly options: FrameHostOptions) {}

  /** frameをDOMへ入れる前に呼ぶ。handshakeを取りこぼさないため。 */
  attach(iframe: HTMLIFrameElement): void {
    this.iframe = iframe;
    this.dead = false;
    window.addEventListener('message', this.onWindowMessage);
  }

  detach(reason: string): void {
    this.dead = true;
    this.stopListening();
    this.teardown();
    this.iframe = null;
    this.options.onEvent({ type: 'detached', reason });
  }

  private stopListening(): void {
    window.removeEventListener('message', this.onWindowMessage);
  }

  private teardown(): void {
    this.port?.close();
    this.port = null;
    for (const subscription of this.subscriptions.values()) {
      subscription.stream.close();
      subscription.abort.abort();
    }
    this.subscriptions.clear();
    for (const abort of this.pendingAborts) abort.abort();
    this.pendingAborts.clear();
    this.inFlight = 0;
  }

  /**
   * frameが別文書へ移ったときの後始末。
   * loadイベントの回数では判別できない（Shellがsrcdocを入れる過程でも複数回起きる）ので、
   * 先に走る信頼bootstrapがpagehideで送る合図を使う。
   * なお遷移先の文書はnonceを知らないため、この経路が無くても新しいportは受け取れない。
   */
  private handleUnloading(): void {
    if (this.dead) return;
    this.dead = true;
    this.stopListening();
    this.teardown();
    this.options.onEvent({ type: 'navigated' });
  }

  private handleWindowMessage(event: MessageEvent): void {
    if (this.dead) {
      if (isReadyMessage(event.data)) {
        this.options.onEvent({ type: 'handshake-rejected', reason: 'already-connected' });
      }
      return;
    }
    // portを渡した後はwindow messageをRPCに使わない。
    if (this.port) {
      if (isReadyMessage(event.data) && event.source === this.iframe?.contentWindow) {
        this.options.onEvent({ type: 'handshake-rejected', reason: 'already-connected' });
      }
      return;
    }
    const frameWindow = this.iframe?.contentWindow ?? null;
    if (!frameWindow || event.source !== frameWindow) {
      if (isReadyMessage(event.data)) {
        this.options.onEvent({ type: 'handshake-rejected', reason: 'source' });
      }
      return;
    }
    // opaque originのiframeからのpostMessageは origin が文字列 'null' になる。
    if (event.origin !== 'null') {
      this.options.onEvent({ type: 'handshake-rejected', reason: 'origin' });
      return;
    }
    if (!isReadyMessage(event.data)) {
      this.options.onEvent({ type: 'handshake-rejected', reason: 'protocol' });
      return;
    }
    if (event.data.nonce !== this.options.nonce) {
      // 置き換えられる前の文書から遅れて届いたready。ここでportを渡すと、
      // 新しい文書が接続できないまま古い文書だけがShellと話せる状態になる。
      this.options.onEvent({ type: 'handshake-rejected', reason: 'stale-document' });
      return;
    }

    const channel = new MessageChannel();
    this.port = channel.port1;
    this.port.onmessage = (message) => {
      if (isUnloadingMessage(message.data)) {
        this.handleUnloading();
        return;
      }
      // 例外がport.onmessageの外へ漏れると、要求が無応答のまま残る。
      this.handleGuestMessage(message.data).catch((error: unknown) => {
        this.reject('upstream_error', error instanceof Error ? error.message : String(error));
      });
    };
    this.port.start();
    // opaque originへのtargetOriginは '*' しか使えないが、送信先windowは固定している。
    frameWindow.postMessage({ protocol: PROTOCOL, type: 'port' }, '*', [channel.port2]);
    this.options.onEvent({ type: 'connected' });
  }

  private async handleGuestMessage(raw: unknown): Promise<void> {
    if (exceedsMessageLimit(raw)) {
      const message = `メッセージが${LIMITS.maxMessageBytes}byteを超えています`;
      this.reject('payload_too_large', message);
      // idが読める場合は応答も返す。返さないとguest側は10秒timeoutまで理由が分からない。
      const id = readRequestId(raw);
      if (id) this.respondError(id, { code: 'payload_too_large', message, traceId: '' });
      return;
    }
    const parsed = parseRequest(raw);
    if (!parsed.ok) {
      this.reject(parsed.error.code, parsed.error.message);
      const id = readRequestId(raw);
      if (id) this.respondError(id, parsed.error);
      return;
    }
    const request = parsed.value;

    if (this.inFlight >= LIMITS.maxConcurrentRequests) {
      this.respondError(request.id, {
        code: 'too_many_requests',
        message: `同時要求は${LIMITS.maxConcurrentRequests}件までです`,
        traceId: '',
      });
      return;
    }

    this.inFlight += 1;
    const abort = new AbortController();
    this.pendingAborts.add(abort);
    const timer = setTimeout(() => abort.abort(), timeoutFor(request.operation));
    try {
      const value = await this.dispatch(request, abort.signal);
      if (exceedsMessageLimit(value)) {
        // 黙って切り詰めない。呼び出し側が別の方法を選べるようにエラーで返す。
        throw new GuestRejection(
          'payload_too_large',
          `応答が${LIMITS.maxMessageBytes}byteを超えました`,
        );
      }
      this.respondOk(request.id, value);
      this.options.onEvent({ type: 'request', operation: request.operation, ok: true, code: null });
    } catch (error) {
      const problem = toProtocolError(error, abort.signal.aborted);
      if (problem.code === 'stale_client') this.options.onEvent({ type: 'stale-client' });
      this.respondError(request.id, problem);
      this.options.onEvent({
        type: 'request',
        operation: request.operation,
        ok: false,
        code: problem.code,
      });
    } finally {
      clearTimeout(timer);
      this.pendingAborts.delete(abort);
      this.inFlight -= 1;
    }
  }

  private async dispatch(request: RequestEnvelope, signal: AbortSignal): Promise<unknown> {
    const { api, workspaceId, appId } = this.options;
    const payload = request.payload;

    switch (request.operation) {
      case 'rpc':
        return api.callRpc(
          workspaceId,
          appId,
          { method: payload.method as string, input: payload.input as Record<string, unknown> },
          { ...this.ifMatch(), signal },
        );

      case 'config.get':
        return api.getPublicAppConfiguration(workspaceId, appId, { signal });

      case 'tasks.get':
        return this.loadOwnedTask(payload.requestId as string, signal);

      case 'tasks.answer': {
        // 所属確認をShellでも行う。frameに固定したapp以外のTaskへ回答させない。
        await this.loadOwnedTask(payload.requestId as string, signal);
        return api.answerTask(
          workspaceId,
          payload.requestId as string,
          {
            expectedVersion: payload.expectedVersion as number,
            answer: payload.answer as Record<string, unknown>,
          },
          { idempotencyKey: payload.idempotencyKey as string, ...this.ifMatch(), signal },
        );
      }

      case 'runs.start':
        return api.startRun(
          workspaceId,
          appId,
          {
            processKey: payload.processKey as string,
            input: payload.input as Record<string, unknown>,
          },
          { idempotencyKey: payload.idempotencyKey as string, ...this.ifMatch(), signal },
        );

      case 'runs.get':
        return this.loadOwnedRun(payload.runId as string, signal);

      case 'runs.diagram': {
        // RunDiagramはappIdを持たないので、先にrunの所属を確かめる。
        await this.loadOwnedRun(payload.runId as string, signal);
        return api.getRunDiagram(workspaceId, payload.runId as string, { signal });
      }

      case 'assets.get':
        return this.loadAsset(payload.assetId as string, signal);

      case 'subscribe':
        return this.subscribe(
          payload.subscriptionId as string,
          payload.filter as { runId?: string; requestId?: string },
        );

      case 'unsubscribe':
        return this.unsubscribe(payload.subscriptionId as string);
    }
  }

  private ifMatch(): { ifMatch?: string } {
    return this.options.clientDigest ? { ifMatch: this.options.clientDigest } : {};
  }

  private async loadOwnedTask(requestId: string, signal: AbortSignal): Promise<TaskRequest> {
    const task = await this.options.api.getTask(this.options.workspaceId, requestId, { signal });
    if (task.appId !== this.options.appId) {
      throw new GuestRejection('forbidden_resource', 'このappのTaskではありません');
    }
    return task;
  }

  private async loadOwnedRun(runId: string, signal: AbortSignal) {
    const run = await this.options.api.getRun(this.options.workspaceId, runId, { signal });
    if (run.appId !== this.options.appId) {
      throw new GuestRejection('forbidden_resource', 'このappのrunではありません');
    }
    return run;
  }

  private async loadAsset(assetId: string, signal: AbortSignal) {
    const content: AssetContent = await this.options.api.getAssetContent(
      this.options.workspaceId,
      this.options.appId,
      assetId,
      { signal },
    );
    return {
      id: assetId,
      mediaType: content.mediaType,
      byteSize: content.bytes.byteLength,
      base64: toBase64(content.bytes),
    };
  }

  private subscribe(
    subscriptionId: string,
    filter: { runId?: string; requestId?: string },
  ): { subscriptionId: string } {
    if (this.subscriptions.has(subscriptionId)) {
      throw new GuestRejection('invalid_request', 'subscriptionIdが重複しています');
    }
    if (this.subscriptions.size >= LIMITS.maxSubscriptions) {
      throw new GuestRejection(
        'too_many_subscriptions',
        `購読は${LIMITS.maxSubscriptions}件までです`,
      );
    }
    const abort = new AbortController();
    const stream = new EventStream(
      this.options.api,
      {
        workspaceId: this.options.workspaceId,
        // iframeではappId必須。guestが指定したappIdは受け付けない。
        filter: { appId: this.options.appId, ...filter },
      },
      {
        onEvent: (event) => this.forwardEvent(subscriptionId, event),
        onState: () => {
          // 接続状態はShellの関心事で、guestへは通知しない（契約にeventしか無い）。
        },
      },
    );
    this.subscriptions.set(subscriptionId, { stream, abort });
    void stream.start();
    return { subscriptionId };
  }

  private unsubscribe(subscriptionId: string): { subscriptionId: string } {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) {
      throw new GuestRejection('unknown_subscription', '購読が見つかりません');
    }
    subscription.stream.close();
    subscription.abort.abort();
    this.subscriptions.delete(subscriptionId);
    return { subscriptionId };
  }

  private forwardEvent(subscriptionId: string, event: PlatformEvent): void {
    if (!this.subscriptions.has(subscriptionId)) return;
    const guestEvent: GuestEvent = {
      id: event.id,
      type: event.type,
      runId: event.runId,
      requestId: event.requestId,
      status: event.status,
      cursor: event.cursor,
      occurredAt: event.occurredAt,
    };
    this.port?.postMessage({
      protocol: PROTOCOL,
      type: 'event',
      subscriptionId,
      event: guestEvent,
    });
  }

  private respondOk(id: string, value: unknown): void {
    this.port?.postMessage({ protocol: PROTOCOL, type: 'response', id, ok: true, value });
  }

  private respondError(id: string, error: ProtocolError): void {
    this.port?.postMessage({ protocol: PROTOCOL, type: 'response', id, ok: false, error });
  }

  private reject(code: string, message: string): void {
    this.options.onEvent({ type: 'request-rejected', code, message });
  }
}

/** Shellが自分で決めた拒否。APIのproblemとは区別する。 */
class GuestRejection extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'GuestRejection';
  }
}

function toProtocolError(error: unknown, aborted: boolean): ProtocolError {
  if (error instanceof GuestRejection) {
    return { code: error.code, message: error.message, traceId: '' };
  }
  if (error instanceof ApiError) {
    // 412はclient artifactが入れ替わった合図。guestには再読込が要ることだけ伝える。
    const code = error.status === 412 ? 'stale_client' : error.problem.code;
    return { code, message: error.problem.detail || error.problem.title, traceId: error.problem.traceId };
  }
  if (aborted) {
    return { code: 'timeout', message: '応答が制限時間内に返りませんでした', traceId: '' };
  }
  return {
    code: 'upstream_error',
    message: error instanceof Error ? error.message : String(error),
    traceId: '',
  };
}

function readRequestId(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const id = (raw as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 && id.length <= 128 ? id : null;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  // btoaはlatin1文字列を取るので、1バイトずつ詰める。大きなassetでもstack上限を超えないよう分割する。
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}
